import { FC, useEffect, useMemo, useState } from "react";
import { Modal, CircularProgress, Box, Typography, IconButton } from "@mui/material";
import { X, RotateCcw, Info, Plus } from "lucide-react";

import {
  getUserNotesByIdAction,
  addUserNoteAction,
} from "../../../redux/actions/customerAction";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useParams } from "react-router-dom";
import {
  NoteOccasion,
  NoteOccasionLabels,
} from "../../../typings/enum/noteOccasions";
import UserNotesCard from "./userNotesCard";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { Colors } from "../../../configs";

export interface INotesModalProps {
  open: boolean;
  onClose(): void;
}

function SoftCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onChange}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        borderRadius: "0.75rem",
        px: "0.75rem",
        py: "0.5rem",
        textAlign: "left",
        background: "none",
        border: "none",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": {
          backgroundColor: Colors.WHITE,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "1.25rem",
          height: "1.25rem",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0.375rem",
          border: `1.5px solid ${checked ? Colors.PRIMARY : Colors.SECONDARY}`,
          backgroundColor: checked ? Colors.PRIMARY : Colors.WHITE,
          transition: "all 0.15s",
        }}
      >
        {checked && (
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m20 6-11 11-5-5" />
          </svg>
        )}
      </Box>
      <Typography
        sx={{ fontSize: "var(--fs-base)", fontWeight: 500, color: Colors.MAIN_GRAY }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function RadioPill({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        borderRadius: "2rem",
        border: `1px solid ${active ? Colors.SECONDARY : Colors.ACCENT_GRAY}`,
        px: "1rem",
        py: "0.5rem",
        fontSize: "var(--fs-base)",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.15s",
        backgroundColor: active ? `${Colors.SECONDARY}18` : Colors.WHITE,
        color: active ? Colors.SECONDARY : Colors.MAIN_GRAY,
        boxShadow: active ? `0 1px 4px ${Colors.SECONDARY}26` : "none",
        "&:hover": {
          backgroundColor: active ? `${Colors.SECONDARY}18` : Colors.BACKGROUND_GRAY,
        },
        "&:active": { transform: "scale(0.98)" },
      }}
    >
      {icon}
      <span>{label}</span>
    </Box>
  );
}

const UserNotesModal: FC<INotesModalProps> = ({ open, onClose }) => {
  const [newNote, setNewNote] = useState("");
  const [selectedOccasions, setSelectedOccasions] = useState<string[]>([]);
  const [noteType, setNoteType] = useState<"oneTime" | "repeatedly">("repeatedly");

  const dispatch = useAppDispatch();
  const params = useParams();
  const customerId = params.id;
  const { customerNotes, addUserNoteLoading } = useAppSelector(
    (state) => state.customer
  );
  const { user } = useAppSelector((state) => state.auth);
  const snackbar = useSnackbarContext();

  useEffect(() => {
    if (customerId) {
      dispatch(getUserNotesByIdAction(customerId as string));
    }
  }, [customerId]);

  const canSave = useMemo(
    () => newNote.trim().length > 0 && selectedOccasions.length > 0,
    [newNote, selectedOccasions]
  );

  const handleCheckboxChange = (key: string) => {
    setSelectedOccasions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleOnAddNote = async () => {
    if (newNote.trim().length === 0) {
      snackbar.showSnackbar("Cannot add empty notes", "warning");
      return;
    }
    if (customerId && user) {
      await dispatch(
        addUserNoteAction({
          note: newNote,
          occasions: selectedOccasions as NoteOccasion[],
          merchantUserId: 1,
          customerId: Number(customerId),
        })
      );
      setNewNote("");
      setSelectedOccasions([]);
      dispatch(getUserNotesByIdAction(customerId as string));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          p: "1.5rem",
        }}
      >
        <Box
          sx={{
            width: "700px",
            maxWidth: "94vw",
            overflow: "hidden",
            borderRadius: "2rem",
            backgroundColor: Colors.WHITE,
            boxShadow: "0 20px 56px rgba(0,0,0,0.10)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              borderBottom: `1px solid ${Colors.ACCENT_GRAY}`,
              px: "2rem",
              py: "1.5rem",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "var(--fs-xs)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                  color: Colors.SECONDARY,
                  lineHeight: 1.3,
                }}
              >
                Customer
              </Typography>
              <Typography
                sx={{
                  fontSize: "var(--fs-lg)",
                  fontWeight: 700,
                  color: Colors.TEXT_GRAY_DARK,
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                Notes
              </Typography>
              <Typography
                sx={{
                  mt: "0.25rem",
                  fontSize: "var(--fs-base)",
                  color: Colors.ACCENT_GRAY_DISABLED,
                }}
              >
                Customer alerts and service reminders
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                color: Colors.MAIN_GRAY,
                mt: "-0.25rem",
                "&:hover": { backgroundColor: Colors.BACKGROUND_GRAY },
              }}
            >
              <X size={22} />
            </IconButton>
          </Box>

          <Box sx={{ px: "2rem", py: "1.5rem" }}>
            {/* Existing notes list */}
            <Box
              data-notes-scroll
              sx={{
                maxHeight: "14rem",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {addUserNoteLoading ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", py: "2.5rem" }}
                >
                  <CircularProgress size={28} sx={{ color: Colors.PRIMARY }} />
                </Box>
              ) : !customerNotes || customerNotes.length === 0 ? (
                <Box sx={{ py: "2.5rem", textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: "var(--fs-base)", color: Colors.ACCENT_GRAY_DISABLED }}
                  >
                    No notes for this customer
                  </Typography>
                </Box>
              ) : (
                customerNotes.map((note) => (
                  <UserNotesCard
                    key={note.id}
                    noteId={note.id}
                    description={note.note}
                    noteBy={`${note.reportedUserFirstName} ${note.reportedUserLastName}`}
                    lastEditedBy={`${note.reportedUserFirstName} ${note.reportedUserLastName}`}
                    date={
                      note.createdTime
                        ? new Date(note.createdTime)
                        : note.lastModifiedTime
                        ? new Date(note.lastModifiedTime)
                        : null
                    }
                    occasions={note.noteShowOccasion as NoteOccasion[]}
                  />
                ))
              )}
            </Box>

            <Box
              sx={{ my: "1.5rem", height: "1px", backgroundColor: Colors.ACCENT_GRAY }}
            />

            {/* Add new note section */}
            <Box
              sx={{
                borderRadius: "1.5rem",
                backgroundColor: Colors.BACKGROUND_GRAY,
                p: "1.25rem",
                border: `1px solid ${Colors.ACCENT_GRAY}`,
              }}
            >
              <Box
                sx={{
                  mb: "1rem",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "var(--fs-md)",
                      fontWeight: 700,
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    Add new note
                  </Typography>
                  <Typography
                    sx={{
                      mt: "0.125rem",
                      fontSize: "var(--fs-base)",
                      color: Colors.ACCENT_GRAY_DISABLED,
                    }}
                  >
                    Choose how often and where this note should appear.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: "0.5rem" }}>
                  <RadioPill
                    active={noteType === "oneTime"}
                    icon={<Info size={15} />}
                    label="One Time"
                    onClick={() => setNoteType("oneTime")}
                  />
                  <RadioPill
                    active={noteType === "repeatedly"}
                    icon={<RotateCcw size={15} />}
                    label="Repeatedly"
                    onClick={() => setNoteType("repeatedly")}
                  />
                </Box>
              </Box>

              <Box
                component="textarea"
                value={newNote}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setNewNote(e.target.value)
                }
                placeholder="Type note here..."
                sx={{
                  height: "7rem",
                  width: "100%",
                  resize: "none",
                  borderRadius: "1rem",
                  border: `1px solid ${Colors.ACCENT_GRAY}`,
                  backgroundColor: Colors.WHITE,
                  px: "1rem",
                  py: "0.75rem",
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                  outline: "none",
                  fontFamily: "var(--font-sans)",
                  display: "block",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  "&::placeholder": { color: Colors.ACCENT_GRAY_DISABLED },
                  "&:focus": {
                    borderColor: Colors.SECONDARY,
                    boxShadow: `inset 0 1px 3px rgba(0,0,0,0.06), 0 0 0 3px ${Colors.SECONDARY}18`,
                  },
                }}
              />

              {/* When to show */}
              <Box sx={{ mt: "1.25rem" }}>
                <Typography
                  sx={{
                    fontSize: "var(--fs-md)",
                    fontWeight: 700,
                    color: Colors.TEXT_GRAY_DARK,
                  }}
                >
                  When to show
                </Typography>
                <Box
                  sx={{
                    mt: "0.75rem",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    columnGap: "2rem",
                    rowGap: "0.25rem",
                    borderRadius: "1rem",
                    backgroundColor: `${Colors.ACCENT_GRAY}33`,
                    p: "0.75rem",
                  }}
                >
                  {Object.keys(NoteOccasion).map((key) => (
                    <SoftCheckbox
                      key={key}
                      label={NoteOccasionLabels[key as keyof typeof NoteOccasion]}
                      checked={selectedOccasions.includes(key)}
                      onChange={() => handleCheckboxChange(key)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  mt: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setNewNote("");
                    setSelectedOccasions([]);
                  }}
                  sx={{
                    borderRadius: "1rem",
                    border: `1px solid ${Colors.ACCENT_GRAY}`,
                    backgroundColor: Colors.WHITE,
                    px: "1.5rem",
                    py: "0.75rem",
                    fontSize: "var(--fs-base)",
                    fontWeight: 700,
                    color: Colors.MAIN_GRAY,
                    cursor: "pointer",
                    transition: "background-color 0.15s",
                    "&:hover": { backgroundColor: Colors.BACKGROUND_GRAY },
                    "&:active": { transform: "scale(0.98)" },
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={!canSave || addUserNoteLoading}
                  onClick={handleOnAddNote}
                  sx={{
                    display: "flex",
                    minWidth: "11.875rem",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    borderRadius: "1rem",
                    px: "1.5rem",
                    py: "0.75rem",
                    fontSize: "var(--fs-base)",
                    fontWeight: 700,
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: Colors.PRIMARY,
                    color: Colors.WHITE,
                    boxShadow: "0 8px 20px rgba(1,135,82,0.28)",
                    "&:hover": {
                      boxShadow: "0 12px 28px rgba(1,135,82,0.36)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "scale(0.98)" },
                    "&:disabled": {
                      cursor: "not-allowed",
                      backgroundColor: Colors.ACCENT_GRAY,
                      color: `${Colors.MAIN_GRAY}8C`,
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                >
                  {addUserNoteLoading ? (
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  ) : (
                    <Plus size={18} />
                  )}
                  Add New Note
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserNotesModal;
